import React from "react";
import { useTranslation } from "react-i18next";
import Banner from "@/components/ui/banner";
import Text from "@/components/ui/text";
import IconContainer from "@/components/ui/icon-container";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { AlertBubbleWarning, Close, Regenerate } from "@dreamhub/designsystem/icons";

interface Props {
  onRegenerate: () => void;
  onDismiss: () => void;
  // Why Regenerate is unavailable, e.g. the contract's terms are incomplete.
  blockedReason?: string;
  // Regenerate is held while another action on the proposal runs.
  disabled?: boolean;
}

export const ProposalOutOfDateBanner: React.FC<Props> = ({
  onRegenerate,
  onDismiss,
  blockedReason,
  disabled = false
}) => {
  const { t } = useTranslation();
  return (
    <Banner variant="error" className="p-2 border shadow-tooltip">
      <div
        className="flex items-center gap-2 min-w-0"
        role="status"
        data-test="proposals-out-of-date"
      >
        <IconContainer size="5" color="negative">
          <AlertBubbleWarning />
        </IconContainer>
        <Text variant="body" className="flex-1 min-w-0">
          {t("contracts.proposals.outOfDate.message")}
        </Text>
        <Button
          variant="secondary"
          // The shared secondary fill is opaque; on the red wash it must let the red through.
          className="bg-white-6 border-border-border1 font-normal"
          onClick={onRegenerate}
          disabled={disabled || !!blockedReason}
          tooltipText={blockedReason}
          data-test="proposals-out-of-date-regenerate"
        >
          <IconContainer size="5" color="secondary">
            <Regenerate />
          </IconContainer>
          {t("contracts.proposals.outOfDate.regenerate")}
        </Button>
        <IconButton
          variant="iconNoBg"
          size="xs"
          onClick={onDismiss}
          aria-label={t("contracts.proposals.outOfDate.dismiss")}
          data-test="proposals-out-of-date-dismiss"
        >
          <IconContainer size="sm">
            <Close />
          </IconContainer>
        </IconButton>
      </div>
    </Banner>
  );
};
